// src/services/ringService.js
// DIAGNOSTIC VERSION - Enhanced logging to identify queue write failure

import {
  collection,
  doc,
  setDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';

/**
 * Creates or retrieves a ring in the unified processing system
 * This automatically queues the ring for CNN processing (via processingQueue)
 */
export const createOrGetRing = async (source, sourceId, imageUrl, metadata = {}) => {
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      throw new Error('Not signed in — cannot create ring without an authenticated jeweller.');
    }

    const ringId = `${source}_${sourceId}`;
    console.log(`🔍 [DIAGNOSTIC] Starting createOrGetRing for: ${ringId}`);
    console.log(`👤 [DIAGNOSTIC] Authenticated jewellerId: ${uid}`);

    // Check if ring already exists
    const ringRef = doc(db, 'rings', ringId);
    const existingRing = await getDoc(ringRef);

    if (existingRing.exists()) {
      console.log(`✅ [DIAGNOSTIC] Ring ${ringId} already exists in CNN system`);
      // Still enqueue in case processing hasn't happened yet (idempotent behavior up to server)
      await queueForProcessing(ringId, imageUrl);
      return {
        isNew: false,
        ringId,
        data: existingRing.data()
      };
    }

    // Create ring document in /rings collection
    // IMPORTANT: include jewellerId (rules require request.resource.data.jewellerId == auth.uid)
    const ringData = {
      id: ringId,
      jewellerId: uid,                   // ← required by Firestore rules
      source,
      sourceId,
      imageUrl,
      thumbnailUrl: metadata.thumbnailUrl || imageUrl,
      styleProfile: null,
      dominantStyle: null,
      styleProcessed: false,
      processingStatus: 'pending',
      modelVersion: '1.0',
      metadata: { ...metadata },
      globalStats: {
        totalViews: 0,
        totalLikes: 0,
        totalDislikes: 0,
        totalSaves: 0,
        avgSwipeTime: 0
      },
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      status: 'active'
    };

    console.log(`📝 [DIAGNOSTIC] Writing ring document...`);
    await setDoc(ringRef, ringData);
    console.log(`✅ [DIAGNOSTIC] Ring document write completed for: ${ringId}`);

    // Verify the write actually persisted
    console.log(`🔍 [DIAGNOSTIC] Verifying ring write to Firestore...`);
    const verification = await getDoc(ringRef);
    if (verification.exists()) {
      console.log(`✅ [DIAGNOSTIC] Ring write VERIFIED in Firestore`);
    } else {
      console.error(`❌ [DIAGNOSTIC] Ring write FAILED - document not found after setDoc!`);
    }

    // Queue for CNN processing via processingQueue
    await queueForProcessing(ringId, imageUrl);

    return { isNew: true, ringId, data: ringData };
  } catch (error) {
    console.error('❌ [DIAGNOSTIC] Error in createOrGetRing:', error);
    console.error('Error name:', error.name);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', JSON.stringify(error, null, 2));
    throw error;
  }
};

/**
 * Queues a ring for CNN style processing.
 * UPDATED: Write directly to processingQueue for CNN processing.
 */
const queueForProcessing = async (ringId, imageUrl, priority = 'normal') => {
  try {
    const auth = getAuth();
    const uid = auth.currentUser?.uid;
    if (!uid) {
      throw new Error('Not signed in — cannot enqueue ring without an authenticated jeweller.');
    }

    console.log(`\n========== QUEUE DIAGNOSTIC START ==========`);    
    console.log(`🎯 [DIAGNOSTIC] Attempting to enqueue ring via processingQueue: ${ringId}`);
    console.log(`👤 [DIAGNOSTIC] jewellerId for queue: ${uid}`);
    console.log(`⏱️  [DIAGNOSTIC] Timestamp: ${new Date().toISOString()}`);

    // Create a reference with auto-generated ID in processingQueue
    const queueRef = doc(collection(db, 'processingQueue'));
    console.log(`📝 [DIAGNOSTIC] Generated processingQueue document ID: ${queueRef.id}`);
    console.log(`📂 [DIAGNOSTIC] Collection path: ${queueRef.path}`);

    // Required fields for processingQueue
    const queueData = {
      ringId,
      imageUrl,
      type: 'style_processing',
      priority,
      status: 'pending',
      attempts: 0,
      maxAttempts: 3,
      createdAt: serverTimestamp(),
      // diagnostics
      diagnosticTimestamp: new Date().toISOString(),
      diagnosticSource: 'ringService.js'
    };

    console.log(`📦 [DIAGNOSTIC] Queue data to write:`, JSON.stringify(queueData, null, 2));

    // Attempt the write
    console.log(`⏳ [DIAGNOSTIC] Calling setDoc to processingQueue...`);
    const writeStartTime = Date.now();
    await setDoc(queueRef, queueData);
    const writeEndTime = Date.now();
    console.log(`✅ [DIAGNOSTIC] processingQueue setDoc() completed in ${writeEndTime - writeStartTime}ms`);

    console.log(`========== QUEUE DIAGNOSTIC END ==========\n`);
    console.log(`Ring ${ringId} enqueued via processingQueue for CNN processing`);
    return queueRef.id;
  } catch (error) {
    console.error(`❌ [DIAGNOSTIC] Error in queueForProcessing for ${ringId}:`, error);
    console.error('Error name:', error.name);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);

    if (error.code === 'permission-denied') {
      console.error(`❌ [DIAGNOSTIC] PERMISSION DENIED - Check /processingQueue create rule.`);
    } else if (error.code === 'unavailable') {
      console.error(`❌ [DIAGNOSTIC] FIRESTORE UNAVAILABLE - Network or connectivity issue`);
    } else if (error.code === 'failed-precondition') {
      console.error(`❌ [DIAGNOSTIC] FAILED PRECONDITION - Missing index or invalid data`);
    }

    console.error('Full error object:', JSON.stringify(error, null, 2));
    console.log(`========== QUEUE DIAGNOSTIC END (ERROR) ==========\n`);

    throw error;
  }
};
